import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EmailIcon from 'src/assets/icons/EmailIcon';
import MoneyIcon from 'src/assets/icons/MoneyIcon';
import PhoneIcon from 'src/assets/icons/PhoneIcon';
import UserIcon from 'src/assets/icons/UserIcon';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import { WorkAreaContext } from 'src/sections/work_area/WorkAreaContext';
import { useContextSelector } from 'use-context-selector';

interface UserProfileData {
  id?: number | string;
  realClientId?: number;
  firstName?: string;
  lastName?: string;
  initials?: string;
  userNameAndRole?: string;
  role?: string;
  title?: string;
  spendingLimit?: string;
  contact?: string;
  emailAddress?: string;
  loginName?: string;
}

interface ViewProfileDialogContentProps {
  userData: UserProfileData | undefined;
  isSuperAdmin: boolean;
}

const getFullName = (
  firstName?: string,
  lastName?: string,
  userNameAndRole?: string,
  fallback?: string
): string => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (userNameAndRole) {
    const parts = userNameAndRole.split(' - ');
    return parts[0] || userNameAndRole;
  }
  return fallback || 'Unknown User';
};

const ViewProfileDialogContent: FC<ViewProfileDialogContentProps> = ({
  userData,
  isSuperAdmin,
}) => {
  const { t } = useTranslation();
  const unknownUserLabel = t('unknownUser');
  const fullName = useMemo(
    () =>
      getFullName(
        userData?.firstName,
        userData?.lastName,
        userData?.userNameAndRole,
        unknownUserLabel
      ),
    [unknownUserLabel, userData?.firstName, userData?.lastName, userData?.userNameAndRole]
  );

  const headingText = useMemo(
    () => userData?.userNameAndRole?.trim() || fullName,
    [fullName, userData?.userNameAndRole]
  );

  return (
    <div className="w-full px-4 pt-3 pb-2">
      <h3 className="mb-2 text-base font-semibold leading-snug text-gray-900">{headingText}</h3>

      <div className="flex flex-col gap-2 text-sm leading-5 text-gray-900">
        {userData?.contact && (
          <div className="flex items-center gap-2">
            <PhoneIcon width={18} height={18} className="flex-shrink-0 text-[#1b73a6]" />
            <span>{userData.contact}</span>
          </div>
        )}
        {userData?.emailAddress && (
          <div className="flex items-center gap-2">
            <EmailIcon width={18} height={18} className="flex-shrink-0 text-[#1b73a6]" />
            <span>{userData.emailAddress}</span>
          </div>
        )}
        {userData?.spendingLimit && (
          <div className="flex items-center gap-2">
            <MoneyIcon width={18} height={18} className="flex-shrink-0 text-[#1b73a6]" />
            <span>
              {userData.spendingLimit} ({t('poSpendingLimit')})
            </span>
          </div>
        )}
        {isSuperAdmin && userData?.loginName && (
          <div className="flex items-center gap-2">
            <UserIcon width={18} height={18} className="flex-shrink-0 text-[#1b73a6]" />
            <span>{userData.loginName}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const useViewProfileModal = (userData: UserProfileData | undefined) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { isSuperAdmin } = useAuth();
  const selectedCompanyId = useContextSelector(WorkAreaContext, (ctx) => ctx.selectedCompanyId);
  const getImpersonate = useContextSelector(WorkAreaContext, (ctx) => ctx.getImpersonate);
  const clientId =
    userData?.realClientId != null
      ? Number(userData.realClientId)
      : userData?.id != null && typeof userData.id === 'number'
      ? userData.id
      : undefined;
  const { canImpersonate, isImpersonatedUser, handleImpersonate } = useMemo(
    () => getImpersonate(clientId, selectedCompanyId),
    [getImpersonate, clientId, selectedCompanyId]
  );

  const handleViewProfile = useCallback(() => {
    confirm({
      title: t('userProfile', 'User Profile'),
      content: <ViewProfileDialogContent userData={userData} isSuperAdmin={isSuperAdmin} />,
      confirmButtonText: t(isImpersonatedUser ? 'impersonated' : 'impersonate'),
      confirmDisabled: !canImpersonate,
      confirmColor: 'info',
      cancelButtonText: t('Close'),
      cancelColor: 'info',
      width: '400px',
      height: 'auto',
      actionBarStyles: 'flex justify-end gap-1.5',
      className:
        'work-area-user-profile-modal [&_.k-widget.k-window.k-dialog]:!h-auto [&_.k-dialog-content]:!p-0 [&_.k-dialog-buttongroup]:!px-3 [&_.k-dialog-buttongroup]:!py-1 [&_.k-dialog-buttongroup]:!min-h-0',
    }).then((confirmed) => {
      if (confirmed) handleImpersonate();
    });
  }, [canImpersonate, confirm, handleImpersonate, isImpersonatedUser, isSuperAdmin, t, userData]);

  return handleViewProfile;
};

export default useViewProfileModal;
